/**
 * Prévia do fracionamento — espelho do `domain/production/numbering.ts` da API.
 *
 * Quem decide é o back-end; isto existe só para a tela mostrar antes de salvar
 * como a quantidade será repartida. Se as duas contas divergirem, a certa é a
 * da API — e a divergência é bug aqui, não lá.
 */

/**
 * Distribui `total` entre `partes` sem perder o resto.
 *
 * O sistema antigo fazia divisão inteira e descartava a sobra: 100 peças em 3
 * quebras viravam 99, e a soma das quebras deixava de fechar com a ordem. Aqui
 * o resto vai uma unidade por vez para as primeiras quebras — 100 em 3 dá
 * 34 + 33 + 33.
 */
export const dividirQuantidade = (total, partes) => {
  if (partes <= 0) return []
  if (total <= 0) return Array.from({ length: partes }, () => 0)

  const base = Math.floor(total / partes)
  const resto = total % partes

  return Array.from({ length: partes }, (item, indice) => base + (indice < resto ? 1 : 0))
}

/**
 * Número da quebra: `<númeroDaOrdem>-<sequência>`, com zero à esquerda.
 *
 * O padding pela largura do total mantém a ordenação alfabética igual à
 * numérica — sem ele, `-10` aparece antes de `-2` em toda listagem.
 */
export const numeroDaQuebra = (numeroOrdem, sequencia, total) => {
  const largura = String(total).length
  return `${numeroOrdem}-${String(sequencia).padStart(largura, '0')}`
}

// ------------------------------------------------------ Fator de conversão --

export const TIPOS_DE_PECA = [
  { value: 'PECA', label: 'Peça' },
  { value: 'CAIXA', label: 'Caixa' },
  { value: 'KG', label: 'KG' },
]

export const rotuloDoTipoDePeca = tipo =>
  TIPOS_DE_PECA.find(item => item.value === tipo)?.label || 'Peça'

/**
 * Só `CAIXA` converte — mesma regra do `domain/production/conversao.ts` da API.
 *
 * O sistema antigo se contradizia sobre KG: o C# dividia por `Piece` sempre que
 * o tipo não fosse Peça, e o SQL que alimentava a tela de fracionamento dividia
 * só para Caixa.
 */
export const converteEmEmbalagem = tipo => tipo === 'CAIXA'

/**
 * Quantas embalagens são necessárias para uma quantidade em peças.
 *
 * Arredonda para cima: 100 peças em caixa de 12 são 9 caixas. Caixa partida
 * ainda é caixa a produzir.
 */
export const embalagensNecessarias = (quantidadeEmPecas, piece) => {
  if (!piece || piece <= 0) return null
  if (quantidadeEmPecas <= 0) return 0
  return Math.ceil(quantidadeEmPecas / piece)
}

/**
 * O que a tela precisa para orientar o fracionamento de um produto.
 *
 * `packages` é a sugestão de quantas quebras criar quando se quer uma quebra
 * por embalagem. A quantidade da quebra continua sempre em peças — o fator
 * orienta e rotula, não troca a unidade do dado.
 */
export const conversaoDoProduto = (quantidadeEmPecas, produto) => {
  if (!produto) return null
  const tipo = produto.typePiece || 'PECA'
  const converte = converteEmEmbalagem(tipo)
  return {
    typePiece: tipo,
    piece: produto.piece ?? null,
    packages: converte ? embalagensNecessarias(quantidadeEmPecas, produto.piece) : null,
    unitLabel: converte ? rotuloDoTipoDePeca(tipo) : 'Peça',
  }
}
